// src/lib/engagement.ts
"use client";

import { apiFetch } from "@/lib/api";

export type EngagementType = "video" | "post" | "image" | "gallery" | "profile";

// Some backends use different paths; try in order until one succeeds
function likePaths(type: EngagementType, id: string | number): string[] {
  const sid = encodeURIComponent(String(id));
  if (type === 'profile') {
    return [
      `/api/people/${sid}/like`,
      `/api/profile/${sid}/like`,
      `/api/user/v2/${sid}/like`,
    ];
  }
  if (type === 'video') return [`/api/video/v2/${sid}/like`];
  if (type === 'post') {
    return [
      `/api/post/${sid}/like`,
      `/api/posts/${sid}/like`,
      `/api/Post/${sid}/like`,
      `/api/post/v2/${sid}/like`
    ];
  }
  if (type === 'image') return [`/api/image/${sid}/like`];
  if (type === 'gallery') return [`/api/Gallery/${sid}/like`, `/api/gallery/${sid}/like`];
  return [];
}

export async function toggleLike(
  type: EngagementType,
  id: string | number,
  session: any,
  currentLiked?: boolean
): Promise<{ liked: boolean; likeCount?: number }> {
  const method = currentLiked ? 'DELETE' : 'POST';
  // Primary: use unified engagement endpoint
  try {
    // Prefer local proxy so Authorization is attached server-side
    const res = await apiFetch(`/api/engagement/like`, session, {
      method,
      cache: 'no-store',
      body: JSON.stringify({ type, id: String(id) })
    });
    if (!res.ok) {
      // A refusal used to read as success. apiFetch resolves rather than throws
      // on a non-2xx, and the line below falls back to `!currentLiked` when the
      // body carries no `liked`, so a 400 cannot_like_own_content returned
      // "liked: true" and every caller painted a filled heart the server never
      // recorded. Callers already revert BOTH the heart and the count in their
      // catch, so a refusal is raised on the same channel as a network failure.
      const body: any = await res.json().catch(() => ({}));
      const refused: any = new Error(`engagement like ${res.status}`);
      refused.code = 'like_refused';
      refused.status = res.status;
      refused.reason = typeof body?.error === 'string' ? body.error : undefined;
      throw refused;
    }
    const j = await res.json().catch(() => ({}));
    const liked = typeof j?.liked === 'boolean' ? j.liked : !currentLiked;
    const likeCount = typeof j?.likeCount === 'number' ? j.likeCount : undefined;
    return { liked, likeCount };
  } catch (e: any) {
    // The endpoint answered and said no. The legacy per-resource paths below
    // must not run: they would be a second write attempt against a decision
    // that has already been made, and for video that endpoint writes too.
    if (e?.code === 'like_refused') throw e;
    // Fallbacks: try resource-specific endpoints for legacy compatibility
    const candidates = likePaths(type, id);
    if (candidates.length === 0) throw e;
    let lastErr: any = null;
    for (const path of candidates) {
      try {
        const res = await apiFetch(path, session, { method, cache: 'no-store' });
        // Same rule here: a non-2xx is not a result, so move to the next
        // candidate rather than reporting the refusal as a completed toggle.
        if (!res.ok) { lastErr = new Error(`${path} ${res.status}`); continue; }
        const j = await res.json().catch(() => ({}));
        const liked = typeof j?.isLikedByMe === 'boolean' ? j.isLikedByMe : (typeof j?.liked === 'boolean' ? j.liked : !currentLiked);
        const likeCount = typeof j?.likeCount === 'number' ? j.likeCount : undefined;
        return { liked, likeCount };
      } catch (err) { lastErr = err; }
    }
    throw lastErr || e;
  }
}

export async function getLikeState(type: EngagementType, id: string | number, session?: any): Promise<LikeState | undefined> {
  try {
    const qs = new URLSearchParams({ type, id: String(id) });
    let res: Response;
    if (session) {
      res = await apiFetch(`/api/engagement/state?${qs.toString()}`, session, { method: 'GET', cache: 'no-store' });
    } else {
      // Client-side unauthenticated read via relative path (Next route/proxy)
      res = await fetch(`/api/engagement/state?${qs.toString()}`, { cache: 'no-store' });
    }
    if (!res.ok) return undefined;
    const j: any = await res.json();
    const liked = typeof j?.isLiked === 'boolean' ? j.isLiked : undefined;
    const likeCount = typeof j?.likeCount === 'number' ? j.likeCount : undefined;
    return (typeof liked === 'boolean' || typeof likeCount === 'number') ? { liked: !!liked, likeCount } : undefined;
  } catch { return undefined; }
}

export async function report(
  type: EngagementType | "profile",
  id: string | number,
  session: any,
  reason: string,
  options?: { userIdForProfile?: string }
): Promise<void> {
  let path: string | null = null;
  if (type === "profile") {
    const uid = options?.userIdForProfile || String(id);
    // chat/report expects a userId GUID; use relative path
    const res = await fetch('/api/chat/report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ userId: uid, reason })
    });
    if (!res.ok) throw new Error(`report ${type} ${res.status}`);
    return;
  }
  const sid = encodeURIComponent(String(id));
  switch (type) {
    case "video":   path = `/api/video/v2/${sid}/report`; break;
    case "post":    path = `/api/post/${sid}/report`; break;
    case "image":   path = `/api/image/${sid}/report`; break;
    case "gallery": path = `/api/gallery/${sid}/report`; break;
  }
  if (!path) return;
  const res = await apiFetch(path, session, {
    method: 'POST',
    body: JSON.stringify({ reason }),
    cache: 'no-store'
  });
  if (!res.ok) throw new Error(`report ${type} ${res.status}`);
}

// Optional local store shape if we later add a provider
export type LikeState = { liked: boolean; likeCount?: number };
